import React from "react";

export const AVISO_VALOR = "Consulte o valor na tabela do fornecedor.";

export function normalizarRespostaCatalogo(data) {
  const origem = data && typeof data === "object" ? data : {};
  const lista = key => Array.isArray(origem[key]) ? origem[key].filter(Boolean) : [];
  return {
    ...origem,
    resultados: lista("resultados"),
    acimaDoTeto: lista("acimaDoTeto"),
    modulares: lista("modulares"),
    medidaAConfirmar: lista("medidaAConfirmar"),
    biblioteca: lista("biblioteca"),
    entendeu: limparEntendeu(origem.entendeu),
  };
}

export function limparEntendeu(value) {
  return typeof value === "string" ? value.trim().replace(/^(?:entendi:\s*)+/i, "") : "";
}

function medidaTexto(value) {
  if (value == null || value === "") return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${(value / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (/^\d+(?:[.,]\d+)?$/.test(text)) {
      const cm = Number(text.replace(",", "."));
      if (Number.isFinite(cm)) return `${(cm / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m`;
    }
    return text;
  }
  return "";
}

function textoRegraMedida(value) {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  return ["texto", "descricao", "regra", "mensagem", "motivo", "status", "resultado"]
    .map(key => typeof value[key] === "string" ? value[key].trim() : "")
    .filter(Boolean)
    .join(" · ");
}

function verdadeExplicita(value, keys) {
  return Boolean(value && typeof value === "object" && keys.some(key => value[key] === true));
}

function falsoExplicito(value, keys) {
  return Boolean(value && typeof value === "object" && keys.some(key => value[key] === false));
}

export function informacaoMedida(produto = {}) {
  if (produto.isBiblioteca) return { suporte: "Medida a confirmar", largura: "", detalhe: "" };
  const medidaRegra = textoRegraMedida(produto.medidaRegra);
  const motivo = typeof produto.motivo === "string" ? produto.motivo.trim() : "";
  const aviso = typeof produto.aviso === "string" ? produto.aviso.trim() : "";
  const regra = medidaRegra || (/medida|largura|faz|aproximad/i.test(motivo) ? motivo : "")
    || (/medida|largura|faz|aproximad/i.test(aviso) ? aviso : "");
  const largura = medidaTexto(produto.larguraCm);
  const regraDados = produto.medidaRegra;
  const aproximada = produto.medidaAproximada === true
    || verdadeExplicita(regraDados, ["aproximada", "medidaAproximada"])
    || /aproximad/i.test(regra);
  const naoFaz = falsoExplicito(regraDados, ["faz", "atende", "suporta", "compativel", "compatível"])
    || verdadeExplicita(regraDados, ["naoFaz", "nãoFaz", "nao_faz", "incompativel", "incompatível"])
    || /não\s*faz|nao\s*faz|incompat|fora da medida/i.test(regra);
  const faz = verdadeExplicita(regraDados, ["faz", "atende", "suporta", "compativel", "compatível"])
    || /\b(?:faz|atende|suporta|compat[ií]vel)\b/i.test(regra);
  const suporte = naoFaz
    ? "❌ não faz"
    : aproximada
      ? "≈ aproximada"
      : largura || faz
        ? "✅ faz"
        : "Medida a confirmar";
  const detalhes = [];
  if (regra) detalhes.push(regra);
  const proximasCm = Array.isArray(produto.medidasProximasCm) ? produto.medidasProximasCm : [];
  const proximas = Array.isArray(produto.medidasProximas) ? produto.medidasProximas : [];
  const medidas = [...proximasCm, ...proximas]
    .map(value => medidaTexto(value && typeof value === "object" ? (value.larguraCm ?? value.cm ?? value.medida ?? value.nome) : value))
    .filter(Boolean);
  if (medidas.length) detalhes.push(`mais próximas: ${[...new Set(medidas)].join(" · ")}`);
  return { suporte, largura, detalhe: detalhes.join(" · ") };
}

export function normalizarProdutoCatalogo(item = {}, biblioteca = false) {
  const locais = Array.isArray(item.locais)
    ? item.locais.filter(value => typeof value === "string" || typeof value === "number")
    : typeof item.local === "string" && item.local.trim() ? [item.local.trim()] : [];
  return {
    ...item,
    nome: (typeof item.nome === "string" && item.nome.trim()) || (typeof item.produtoNome === "string" && item.produtoNome.trim()) || "Produto",
    fornecedor: typeof item.fornecedor === "string" ? item.fornecedor : "",
    fotoUrl: [item.fotoUrl, item.url, item.miniaturaUrl].find(value => typeof value === "string" && value.trim()) || "",
    locais,
    isBiblioteca: biblioteca,
  };
}

export function pedidoCatalogo({ texto = "", fornecedor = "", medida = "", foto = null, referenciaProdutoId = null, limite = 30 } = {}) {
  const metros = Number(String(medida).replace(",", "."));
  return {
    texto: texto.trim() || null,
    referenciaProdutoId: referenciaProdutoId || null,
    imagemBase64: foto || null,
    fornecedor: fornecedor || null,
    larguraMaxCm: Number.isFinite(metros) && metros > 0 ? Math.round(metros * 100) : null,
    limite,
  };
}

export function pedidoSimilarCatalogo(produto = {}, { fornecedor = "", medida = "", limite = 30 } = {}) {
  const referenciaProdutoId = produto.isBiblioteca
    ? produto.produtoId
    : produto.id;
  const referenciaValida = typeof referenciaProdutoId === "string" || typeof referenciaProdutoId === "number"
    ? String(referenciaProdutoId).trim()
    : "";
  return pedidoCatalogo({
    texto: referenciaValida ? "" : (produto.nome || produto.produtoNome || ""),
    referenciaProdutoId: referenciaValida || null,
    fornecedor,
    medida,
    limite,
  });
}

export function ControlesBuscaCatalogo({
  texto, onTexto, fornecedores = [], fornecedor = "", onFornecedor, medida = "", onMedida,
  onBuscar, buscando = false, desabilitado = false, foto = null, onFoto, onRemoverFoto,
  reduzirFoto,
  compact = false,
}) {
  const inputFoto = React.useRef(null);
  const campo = {
    padding: compact ? "8px 9px" : "10px 12px",
    borderRadius: 4,
    border: "1px solid var(--catalog-line, #d9d1c2)",
    fontFamily: "inherit",
    fontSize: compact ? 13 : 15,
    background: "#fff",
    color: "inherit",
    minWidth: 0,
    boxSizing: "border-box",
  };
  return (
    <form onSubmit={e => { e.preventDefault(); onBuscar(); }} style={{ display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        <label style={{ display: "flex", flex: "1 1 230px", minWidth: 0, flexDirection: "column", gap: 4, color: "#696253", fontSize: 12, fontWeight: 700 }}>
          Descreva o que procura
          <input aria-label="Descreva o que procura" value={texto} onChange={e => onTexto(e.target.value)}
            disabled={desabilitado} placeholder="sofá de couro até 4 metros"
            style={{ ...campo, width: "100%" }} />
        </label>
        <input ref={inputFoto} type="file" accept="image/*" aria-label="Selecionar foto" style={{ display: "none" }}
          onChange={async e => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            try { onFoto(await reduzirFoto(file)); } catch (error) { onFoto(null, error); }
          }} />
        <button type="button" disabled={desabilitado} onClick={() => inputFoto.current?.click()}
          style={{ ...campo, cursor: desabilitado ? "default" : "pointer", fontWeight: 700 }}>📷 Buscar por foto</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 8 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, color: "#696253", fontSize: 12, fontWeight: 700 }}>
          Fornecedor
          <select aria-label="Fornecedor" value={fornecedor} onChange={e => onFornecedor(e.target.value)} disabled={desabilitado} style={campo}>
            <option value="">Todos</option>
            {fornecedores.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, color: "#696253", fontSize: 12, fontWeight: 700 }}>
          Medida opcional (m)
          <input aria-label="Medida opcional" inputMode="decimal" value={medida} onChange={e => onMedida(e.target.value)}
            disabled={desabilitado} placeholder="Ex.: 4" style={campo} />
        </label>
      </div>
      {foto && <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <img src={foto} alt="Foto de referência" style={{ height: 58, borderRadius: 4, border: "1px solid #d9d1c2" }} />
        <button type="button" onClick={onRemoverFoto} style={{ ...campo, cursor: "pointer" }}>Remover foto</button>
      </div>}
      <div>
        <button type="submit" disabled={desabilitado || buscando}
          style={{ border: "none", borderRadius: 4, padding: compact ? "8px 15px" : "10px 18px", background: "var(--catalog-accent, #70572f)", color: "#fff", fontWeight: 700, fontSize: compact ? 13 : 14, cursor: desabilitado || buscando ? "default" : "pointer", opacity: desabilitado ? 0.55 : 1, fontFamily: "inherit" }}>
          {buscando ? "Buscando…" : "Buscar"}
        </button>
      </div>
    </form>
  );
}

function labelLocal(local) {
  return String(local || "").toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());
}

function ProductCard({ produto, theme, onDetalhes, onSimilares }) {
  const medida = informacaoMedida(produto);
  const line = theme.nuLine || theme.sandDark;
  return (
    <article style={{ border: `1px solid ${line}`, borderRadius: 4, overflow: "hidden", background: "#fff", color: theme.text }}>
      <button type="button" onClick={() => onDetalhes(produto)} style={{ width: "100%", border: 0, padding: 0, background: "none", textAlign: "left", cursor: "pointer", color: "inherit", fontFamily: "inherit" }}>
        {produto.fotoUrl
          ? <img src={produto.fotoUrl} alt={produto.nome || "Produto"} loading="lazy" style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "contain", display: "block", background: theme.sandDark }} />
          : <div style={{ aspectRatio: "4 / 3", background: theme.sandDark, display: "grid", placeItems: "center", color: theme.muted, fontSize: 12 }}>Sem foto</div>}
        <div style={{ padding: "9px 10px 4px" }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{produto.nome || "Produto"}</div>
          {produto.fornecedor && <div style={{ marginTop: 2, color: theme.muted, fontSize: 12 }}>{produto.fornecedor}</div>}
          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700 }}>{medida.suporte}{medida.largura ? ` · ${medida.largura}` : ""}</div>
          {medida.detalhe && <div style={{ marginTop: 3, fontSize: 11, color: theme.muted }}>{medida.detalhe}</div>}
          {Array.isArray(produto.locais) && produto.locais.length > 0 &&
            <div style={{ marginTop: 5, color: theme.positive, fontSize: 11, fontWeight: 700 }}>{produto.locais.map(labelLocal).join(" · ")}</div>}
        </div>
      </button>
       <button type="button" onClick={() => onSimilares(produto)} style={{ margin: "5px 10px 10px", padding: "5px 0", border: 0, background: "none", color: theme.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 12 }}>Ver similares</button>
    </article>
  );
}

export function ResultadosCatalogo({ theme, resposta, onDetalhes, onSimilares, compact = false }) {
  const data = normalizarRespostaCatalogo(resposta);
  const grupos = [
    ["Parecidos", data.resultados],
    ["Outras opções", data.acimaDoTeto],
    ["Modulares", data.modulares],
    ["Medida a confirmar", data.medidaAConfirmar],
    ["Na biblioteca (sem medida oficial)", data.biblioteca],
  ].filter(([, items]) => items.length);
  const empty = grupos.length === 0;
  return (
    <div>
      {data.entendeu && <div style={{ marginTop: 8, color: theme.muted, fontSize: compact ? 12 : 13 }}>Entendi: {data.entendeu}</div>}
      {grupos.map(([title, items]) => (
        <section key={title} style={{ marginTop: compact ? 14 : 20 }}>
          <div style={{ fontWeight: 700, fontSize: compact ? 14 : 16, color: theme.text }}>{title}</div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 138 : 190}px, 1fr))`, gap: 10, marginTop: 8 }}>
            {items.map((item, index) => {
              const isBiblioteca = title === "Na biblioteca (sem medida oficial)";
              const product = normalizarProdutoCatalogo(item, isBiblioteca);
              return <ProductCard key={item.id || item.produtoId || `${title}-${index}`} produto={product} theme={theme} onDetalhes={onDetalhes} onSimilares={onSimilares} />;
            })}
          </div>
        </section>
      ))}
      {empty && <div style={{ marginTop: 16, color: theme.muted, fontSize: 13 }}>Nenhum produto encontrado. Ajuste a descrição ou a medida.</div>}
    </div>
  );
}
